import styled from '@emotion/styled';
import React, { useState } from 'react';
import { Theme, ThemeProps, ExpandableThemeProps, SelectableThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { SpecialFilters } from '@model/filters';
import { mixins } from '@styles/mixins';
import { Radio } from '@components/common/radio';
import { Icons } from '@model/common/icons';
import { Icon } from '@components/common/generic-icon';
import { FilterSubHeading, FilterToolTip, FilterTooltipContent } from '@styles/filters';
import { Filter, FilterOption } from '@model/iceberg';
import { TestId } from '@components/test-ids';
import { getFilterPaddingMixin } from '@styles/filters';

/* ***************** *
 *       Types       *
 * ***************** */
export interface RadioButtonFilterProps {
  theme?: Theme;
  testId?: string;
  filter: Filter;
  onChange: (payload: Filter) => void;
  subHeading?: string;
  tooltip?: string;
  visibleNumberOfFilters?: number;
  isLeftFilter?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
interface ContainerProps extends ThemeProps {
  isLeftFilter?: boolean;
}

const Container: any = styled.div(({ theme, isLeftFilter }: ContainerProps) => ({
  maxWidth: 375,
  ...getFilterPaddingMixin(theme, isLeftFilter),

  ['.MuiIconButton-root']: {
    padding: theme.custom.spacing.xSmall
  }
}));

const FilterHeading = styled.h5(({ theme }: ThemeProps) => ({
  display: 'flex',
  aligItems: 'center',
  justifyContent: 'flex-start',
  ...(theme.custom.typography.labels.default as any),
  lineHeight: '1.8rem',
  margin: 0
}));

const FiltersContainer: any = styled.ul(({ theme, isExpanded }: ExpandableThemeProps) => ({
  padding: 0,
  transition: `margin ${theme.custom.transitions.default}`,
  ...(isExpanded && { marginBottom: 0 })
}));

const FilterRow: any = styled.li(({ theme, isSelected }: SelectableThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  display: 'block',
  ['&:hover']: {
    ['.MuiFormControlLabel-root']: {
      ['label, label > span']: {
        transition: `color ${theme.custom.transitions.default}`,
        color: theme.custom.colors.group1.base
      }
    },

    ['.MuiIconButton-label svg']: {
      transform: 'scale(1)'
    }
  },

  ['.MuiFormControlLabel-root']: {
    ['label, label > span']: {
      transition: `color ${theme.custom.transitions.default}`,
      color: isSelected ? theme.custom.colors.group1.base : theme.custom.colors.black
    }
  },

  ...(isSelected && {
    ['.MuiIconButton-label svg']: {
      transform: 'scale(1)',
      color: theme.custom.colors.group1.base
    }
  })
}));

interface MoreFiltersWrapperProps extends ExpandableThemeProps {
  numOfItems: number;
}
const MoreFiltersWrapper: any = styled.ul(({ theme, isExpanded, numOfItems }: MoreFiltersWrapperProps) => ({
  display: 'block',
  padding: 0,
  margin: 0,
  overflow: 'hidden',
  maxHeight: isExpanded ? numOfItems * 55 + 20 : 20,
  transition: `max-height ${theme.custom.transitions.smooth}`,
  marginTop: 10
}));

const MoreFiltersToggle: any = styled.li(({ theme, isExpanded }: ExpandableThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  ...mixins(theme).link,
  cursor: 'pointer',
  overflowY: 'hidden',
  maxHeight: isExpanded ? 0 : 20,
  transition: `max-height ${theme.custom.transitions.default} 0.4s, color: ${theme.custom.transitions.default}`
}));

export const RadioLabel = styled.label(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  pointerEvents: 'none'
}));

const Count = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  marginLeft: theme.custom.spacing.xSmall
}));

/* *********************** *
 *    SingleSelectFilter   *
 * *********************** */

export const RadioButtonFilter = ({
  filter,
  visibleNumberOfFilters = 4,
  subHeading,
  tooltip,
  onChange,
  testId,
  isLeftFilter
}: RadioButtonFilterProps) => {
  const t: any = useI18NextContext();

  /* *** LOGIC *** */
  const handleChange = (event: any) => {
    const value: string = event.target.value;
    const option = filter.values.filter((f: FilterOption) => f.value === value).shift();
    if (option) {
      const values: Array<FilterOption> = filter.values.map((f: FilterOption) =>
        f.value === value ? { ...f, selected: true } : { ...f, selected: false }
      );
      onChange({ ...filter, values });
    }
  };

  const handleOnSelectAll = () => {
    const values: Array<FilterOption> = filter.values.map((f: FilterOption) => ({ ...f, selected: false }));
    onChange({ ...filter, values });
  };

  /* *** STATE *** */
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false);

  /* *** RENDERERS *** */

  const renderFilterRows = () =>
    filter.values
      .filter((_: FilterOption, index: number) => (showMoreFilters ? !!filter : index + 1 <= visibleNumberOfFilters))
      .map((filter: FilterOption, index: number) => {
        return (
          <FilterRow isSelected={filter.selected} key={`${filter.name}-${index}`}>
            <Radio
              testId={TestId.singleSelectFilters.filterRadio}
              renderLabel={() => (
                <RadioLabel>
                  {filter.name} {!!filter.count && <Count>({filter.count})</Count>}
                </RadioLabel>
              )}
              checked={!!filter.selected}
              onChange={handleChange}
              value={filter.value}
            />
          </FilterRow>
        );
      });

  const isAllSelected: boolean = filter.values.every((f: FilterOption) => !f.selected);
  const numberOfHiddenFilters: number = filter.values.filter(
    (_: FilterOption, index: number) => index + 1 > visibleNumberOfFilters
  )?.length;
  return (
    <Container data-testid={testId || TestId.singleSelectFilters.main} isLeftFilter={isLeftFilter}>
      <FilterHeading data-testid={TestId.singleSelectFilters.header}>
        {t(filter.formattedName)}
        {tooltip && (
          <FilterToolTip content={<FilterTooltipContent>{t(tooltip)}</FilterTooltipContent>}>
            <Icon name={Icons.INFO} />
          </FilterToolTip>
        )}
      </FilterHeading>
      {subHeading && <FilterSubHeading>{t(subHeading)}</FilterSubHeading>}
      <FiltersContainer isExpanded={showMoreFilters}>
        <FilterRow data-testid={TestId.singleSelectFilters.filterRowAll} isSelected={isAllSelected}>
          <Radio
            testId={TestId.singleSelectFilters.filterRadio}
            label={t('filters__title--All')}
            checked={isAllSelected}
            onChange={handleOnSelectAll}
            value={SpecialFilters.ALL}
          />
        </FilterRow>
        {renderFilterRows()}
      </FiltersContainer>
      {!!numberOfHiddenFilters && (
        <MoreFiltersWrapper
          data-testid={TestId.singleSelectFilters.showMoreFilters}
          isExpanded={showMoreFilters}
          numOfItems={numberOfHiddenFilters}
        >
          <MoreFiltersToggle isExpanded={showMoreFilters} onClick={() => setShowMoreFilters(!showMoreFilters)}>
            {t('filters__view-more--title', { count: numberOfHiddenFilters })}
          </MoreFiltersToggle>
        </MoreFiltersWrapper>
      )}
    </Container>
  );
};
